<template>
  <v-menu offset-y>
    <template #activator="{ on: onMenu }">
      <v-hover #default="{ hover }">
        <v-btn
          small
          :color="hover ? btnColorHoverComp : btnColor"
          :depressed="btnDepressed"
          :disabled="btnDisabled"
          :fixed="btnFixed"
          :text="btnFlat"
          :loading="btnLoading"
          :outlined="btnOutlined"
          v-on="onMenu"
          :class="[
            {
              removeBackgroundOnDisabled
            },
            hover ? btnTextColorHoverComp : btnTextColor,
            'segmentedDropdown letter-spacing-none font-weight-regular pr-2 font-12'
          ]"
        >
          <div
            :class="showDivider ? 'pr-2 dropdownDivider' : null"
            class="btn-content-t"
          >
            <slot name="button-content">
              <span v-if="!showIcon">{{
                valueComp[textProp] ? valueComp[textProp] : valueComp
              }}</span>
              <v-icon
                v-else
                small
                :color="btnIconColor"
                >{{ icon }}</v-icon
              >
            </slot>
          </div>
          <div class="pl-1">
            <slot name="button-icon">
              <v-icon
                v-if="showArrow"
                small
                >keyboard_arrow_down</v-icon
              >
            </slot>
          </div>
        </v-btn>
      </v-hover>
    </template>
    <v-list
      :dense="listDense"
      :subheader="listSubheader"
    >
      <template v-for="option in options">
        <!-- LIST DIVIDER -->
        <v-divider
          v-if="option.divider"
          class="my-0"
          :key="option"
        />
        <!-- LIST HEADER -->
        <v-subheader
          v-else-if="option.header"
          class="my-0"
          :key="option"
          >{{ option.header }}</v-subheader
        >
        <v-hover
          v-else
          #default="{ hover }"
          :key="option"
        >
          <v-list-item
            @click="selectValue(option)"
            :class="getListTileClass(option, hover)"
          >
            <!-- LIST CONTENT -->
            <v-list-item-content :class="getListTileTitleClass(option, hover)">
              <slot
                name="option"
                :item="option"
              >
                {{ option[textProp] ? option[textProp] : option }}
              </slot>
            </v-list-item-content>
          </v-list-item>
        </v-hover>
      </template>
    </v-list>
  </v-menu>
</template>

<script>
export default {
  name: 'BaseDropdown',
  props: {
    btnColor: {
      type: String,
      required: false,
      default: 'accent'
    },
    btnColorHover: {
      type: String,
      required: false,
      default: null
    },
    btnTextColor: {
      type: String,
      required: false,
      default: 'white--text'
    },
    btnIconColor: {
      type: String,
      required: false,
      default: 'secondary'
    },
    btnTextColorHover: {
      type: String,
      required: false,
      default: null
    },
    btnDepressed: {
      type: Boolean,
      required: false,
      default: false
    },
    btnDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    btnFixed: {
      type: Boolean,
      required: false,
      default: false
    },
    btnFlat: {
      type: Boolean,
      required: false,
      default: false
    },
    btnLoading: {
      type: Boolean,
      required: false,
      default: false
    },
    btnOutlined: {
      type: Boolean,
      required: false,
      default: false
    },
    showArrow: {
      type: Boolean,
      require: false,
      default: false
    },
    showIcon: {
      type: Boolean,
      required: false,
      default: false
    },
    showDivider: {
      type: Boolean,
      required: false,
      default: false
    },
    removeBackgroundOnDisabled: {
      type: Boolean,
      required: false,
      default: false
    },
    listDense: {
      type: Boolean,
      required: false,
      default: false
    },
    listSubheader: {
      type: Boolean,
      required: false,
      default: false
    },
    listOptBgColor: {
      type: String,
      required: false,
      default: 'white'
    },
    listOptTextColor: {
      type: String,
      required: false,
      default: 'secondary--text'
    },
    listOptBgColorHover: {
      type: String,
      required: false,
      default: null
    },
    listOptTextColorHover: {
      type: String,
      required: false,
      default: 'secondary--text'
    },
    selectedBgColor: {
      type: String,
      required: false,
      default: 'accent15'
    },
    selectedTextColor: {
      type: String,
      required: false,
      default: 'accentHover--text'
    },
    options: {
      type: Array,
      required: true
    },
    value: {
      type: [String, Number, Boolean, Array, Object],
      required: true
    },
    textProp: {
      type: String,
      required: false,
      default: 'text'
    },
    valueProp: {
      type: String,
      required: false,
      default: 'value'
    },
    icon: {
      type: String,
      required: false,
      default: 'keyboard_arrow_down'
    }
  },
  computed: {
    valueComp: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    btnColorHoverComp() {
      return this.btnColorHover ? this.btnColorHover : this.btnColor
    },
    btnTextColorHoverComp() {
      return this.btnTextColorHover ? this.btnTextColorHover : this.btnTextColor
    }
  },
  methods: {
    selectValue(selected) {
      this.valueComp = selected

      this.$emit('option-selected')
    },
    getClass({ color, colorHover, selectedColor, option, hover }) {
      const opt = option[this.valueProp] ? option[this.valueProp] : option
      const value = this.valueComp[this.valueProp]
        ? this.valueComp[this.valueProp]
        : this.valueComp

      if (opt === value) {
        return selectedColor
      } else {
        if (hover) {
          return colorHover
        } else {
          return color
        }
      }
    },
    getListTileClass(option, hover) {
      return this.getClass({
        color: this.listOptBgColor,
        colorHover: this.listOptBgColorHover,
        selectedColor: this.selectedBgColor,
        option,
        hover
      })
    },
    getListTileTitleClass(option, hover) {
      return this.getClass({
        color: this.listOptTextColor,
        colorHover: this.listOptTextColorHover,
        selectedColor: this.selectedTextColor,
        option,
        hover
      })
    }
  }
}
</script>

<!-- Not scoped in order to custom v-btn childs -->
<style>
.segmentedDropdown .v-btn__content {
  height: 100%;
}
</style>

<style scoped>
.segmentedDropdown.removeBackgroundOnDisabled.v-btn.v-btn--disabled {
  background-color: transparent !important;
}
.dropdownDivider {
  display: flex;
  align-items: center;
  border-right: solid 1px;
  height: 100%;
}
</style>
